import { ChevronRight, Layers } from 'lucide-react'
import clsx from 'clsx'
import type { Bucket, DealSearchSchema, GroupSummary, Totals } from './types'
import { count, moneyExact, pct, ratio } from './format'

const TILES: Array<{ key: keyof Totals; label: string; render: (t: Totals) => string }> = [
  { key: 'dealCount', label: 'Deals', render: (t) => count(t.dealCount) },
  { key: 'totalVolume', label: 'Volume', render: (t) => moneyExact(t.totalVolume) },
  { key: 'avgDealSize', label: 'Avg size', render: (t) => moneyExact(t.avgDealSize) },
  { key: 'closedCount', label: 'Closed', render: (t) => count(t.closedCount) },
  { key: 'closedVolume', label: 'Closed vol.', render: (t) => moneyExact(t.closedVolume) },
  { key: 'avgLtv', label: 'Avg LTV', render: (t) => pct(t.avgLtv) },
  { key: 'avgDscr', label: 'Avg DSCR', render: (t) => ratio(t.avgDscr) },
]

/** Aggregates plus a group-by breakdown. Every figure describes the WHOLE
 *  result set — the server computes them separately from the page — and every
 *  bucket is clickable, which is what makes the summary a drill-down rather
 *  than a read-only banner. */
export function SummaryStrip({
  totals,
  groups,
  groupBy,
  schema,
  loading,
  onGroupByChange,
  onDrillDown,
  activeBucketValues,
}: {
  totals: Totals | undefined
  groups: GroupSummary | undefined
  groupBy: string
  schema: DealSearchSchema | undefined
  loading: boolean
  onGroupByChange: (dimension: string) => void
  onDrillDown: (bucket: Bucket) => void
  activeBucketValues: string[]
}) {
  const maxVolume = Math.max(1, ...(groups?.buckets ?? []).map((b) => b.totalVolume))

  return (
    <section
      aria-label="Result summary"
      className="flex flex-shrink-0 flex-col gap-2 rounded-lg border border-amber-300/10 bg-[#0b0b0b] p-2.5"
    >
      <div className="grid grid-cols-4 gap-2 lg:grid-cols-7">
        {TILES.map((tile) => (
          <div
            key={String(tile.key)}
            className="rounded border border-amber-300/[0.07] bg-black/30 px-2.5 py-1.5"
          >
            <div className="text-[9px] uppercase tracking-[0.14em] text-stone-500">{tile.label}</div>
            <div
              className={clsx(
                'mt-0.5 font-mono text-[15px] leading-tight text-stone-100 transition-opacity',
                loading && 'opacity-40',
              )}
            >
              {totals ? tile.render(totals) : '—'}
            </div>
          </div>
        ))}
      </div>

      <div className="flex items-center gap-2 border-t border-amber-300/[0.07] pt-2">
        <label className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.14em] text-stone-500">
          <Layers size={11} />
          Break down by
          <select
            value={groupBy}
            onChange={(event) => onGroupByChange(event.target.value)}
            aria-label="Summary breakdown dimension"
            className="rounded border border-amber-300/10 bg-black/40 px-1.5 py-1 text-[10px] normal-case tracking-normal text-stone-200 focus:border-amber-300/30 focus:outline-none"
          >
            {(schema?.groupDimensions ?? []).map((dimension) => (
              <option key={dimension.key} value={dimension.key}>
                {dimension.label}
              </option>
            ))}
          </select>
        </label>
        {groups?.bucketsOmitted ? (
          <span className="text-[9px] text-stone-600">
            Top {groups.buckets.length - 1} shown · {groups.bucketsOmitted} rolled into “Other”
          </span>
        ) : null}
      </div>

      {groups && groups.buckets.length > 0 ? (
        <ul className="grid max-h-[132px] grid-cols-1 gap-x-3 gap-y-0.5 overflow-y-auto md:grid-cols-2 xl:grid-cols-3">
          {groups.buckets.map((bucket) => {
            const active = bucket.value !== null && activeBucketValues.includes(bucket.value)
            const drillable = bucket.recorded && !bucket.isOverflow
            return (
              <li key={bucket.label}>
                <button
                  type="button"
                  disabled={!drillable}
                  onClick={() => onDrillDown(bucket)}
                  title={
                    drillable
                      ? `Filter to ${bucket.label}`
                      : 'Not a filterable value — nothing recorded for these deals'
                  }
                  className={clsx(
                    'group relative flex w-full items-center gap-2 overflow-hidden rounded px-2 py-1 text-left transition',
                    active
                      ? 'bg-amber-400/[0.12] text-stone-100'
                      : drillable
                        ? 'text-stone-300 hover:bg-white/[0.03]'
                        : 'cursor-default text-stone-500',
                  )}
                >
                  {/* Volume bar sits behind the label — proportion at a glance
                      without spending a separate chart column on it. */}
                  <span
                    aria-hidden
                    className="absolute inset-y-0 left-0 bg-amber-400/[0.06]"
                    style={{ width: `${(bucket.totalVolume / maxVolume) * 100}%` }}
                  />
                  <span className="relative flex-1 truncate text-[11px]">{bucket.label}</span>
                  <span className="relative font-mono text-[10px] text-stone-500">
                    {count(bucket.dealCount)}
                  </span>
                  <span className="relative w-[62px] text-right font-mono text-[10px] text-stone-400">
                    {moneyExact(bucket.totalVolume)}
                  </span>
                  {drillable && (
                    <ChevronRight
                      size={10}
                      className="relative text-stone-700 transition group-hover:text-amber-300"
                    />
                  )}
                </button>
              </li>
            )
          })}
        </ul>
      ) : (
        <p className="px-2 py-1 text-[10px] text-stone-600">
          {loading ? 'Calculating…' : 'No deals to summarize under the current filters.'}
        </p>
      )}
    </section>
  )
}
